import { useCallback, useEffect, useState } from "react";
import { ChevronRight, Briefcase, Home, Wallet, Shield, Scale, Play, List, Plus, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "app/hooks";
import { initSubscriptionProcess } from "features/subscription-process/slices/thunks/initProcessThunk";
import { MRI_ROUTES } from "features/subscription-process/utils/mriRoutes";
import { LEGAL_PROTECTION_ROUTES } from "features/subscription-process/utils/legalProtectionRoutes";
import { BranchCode } from "api/risk";
import { useDialog } from "hooks/useDialog";
import { risksAPI } from "services/swagger-api/riskApi";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { NsKey } from "enums/NsKey";
import Loading from "components/ui/Loader";
import { borrowersAPI } from "services/swagger-api";

interface ReferentialDto {
  code: string;
  label: string;
}

interface ReadRiskMinimalDto {
  riskId: string;
  folderIdentifier: string;
  creationDate: Date;
  label?: string;
}

interface SimulationInProgress extends ReadRiskMinimalDto {
  branchCode: BranchCode;
}

const mapBranchLabel = {
  [BranchCode.Unemployment]: "INSURANCE.UNEMPLOYMENT",
  [BranchCode.UPI]: "INSURANCE.UPI",
  [BranchCode.Borrower]: "INSURANCE.BORROWER",
  [BranchCode.Mri]: "INSURANCE.MRI",
  [BranchCode.RGI]: "INSURANCE.GLI",
  [BranchCode.Pet]: "INSURANCE.PET",
  [BranchCode.LegalProtection]: "INSURANCE.LEGAL_PROTECTION",
}

type ActionType = 'new' | 'continue' | 'list';

const SelectBrokerBranches: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.Common])
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSimulations, setIsLoadingSimulations] = useState(false);
  const [isCreatingSimulation, setIsCreatingSimulation] = useState(false);
  const [hoveredBranch, setHoveredBranch] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<BranchCode | null>(null);
  const [simulationsInProgress, setSimulationsInProgress] = useState<SimulationInProgress[]>([]);
  const [showSimulationsList, setShowSimulationsList] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { closeDialog } = useDialog()

  const [branches, setBranches] = useState<ReferentialDto[]>();

  useEffect(() => {
    const getBrokerBranches = async () => {
      setIsLoading(true)
      try {
        const result = await risksAPI.getBrokerBranches()
        if (result) {
          const brokerBranchesConfig = result.branches.map((branch) => ({
            code: branch.code,
            label: mapBranchLabel[branch.code as BranchCode]
          }))
          setBranches(brokerBranchesConfig)
        }
      } catch (error) {
        console.error('Erreur lors du chargement des branches:', error)
      } finally {
        setIsLoading(false)
      }
    }
    getBrokerBranches()
  }, [])

  const getSimulationsInProgress = async (branchCode: BranchCode) => {
    setIsLoadingSimulations(true);
    try {
      const result = await risksAPI.getAllSimulationInProgress({ branchCode });
      if (result && result.risks) {
        const simulations: SimulationInProgress[] = result.risks.map(risk => ({
          ...risk,
          branchCode
        }));
        setSimulationsInProgress(simulations);
        return simulations;
      }
      return [];
    } catch (error) {
      console.error('Erreur lors du chargement des simulations en cours:', error);
      return [];
    } finally {
      setIsLoadingSimulations(false);
    }
  };

  const navigateToSimulation = (branchCode: BranchCode, riskId?: string) => {
    const riskIdParam = riskId ? `/${riskId}` : '';
    let route: string | undefined
    switch (branchCode) {
      case BranchCode.Unemployment:
        route = `simulation/inscription/chomage${riskIdParam}`
        break;
      case BranchCode.UPI:
        route = `simulation/inscription/cbno${riskIdParam}`
        break;
      case BranchCode.Borrower:
        route = `simulation/profilage/emprunteur/projet${riskIdParam}`
        break;
      case BranchCode.Mri:
        route = `${MRI_ROUTES.registration}${riskIdParam}`
        break;
      case BranchCode.RGI:
        route = `simulation/inscription/gli${riskIdParam}`
        break;
      case BranchCode.LegalProtection:
        route = `${LEGAL_PROTECTION_ROUTES.registration}${riskIdParam}`
        break;
      case BranchCode.Pet:
        // Pas de souscription côté courtier : le parcours animal vit dans
        // l'espace-membre, le courtier consulte uniquement les contrats.
        return;
    }

    if (route) navigate(route);
  };

  const fetchRisk = useCallback(async (branchCode: BranchCode) => {
    const result = await risksAPI.initSimulationByBranchCode({
      createSimulationCommandDto: { branchCode }
    });
    const { riskId, folderIdentifier } = result
    dispatch(initSubscriptionProcess({ branchCode, riskId, folderIdentifier }));
    return riskId
  }, [dispatch])

  const createNewSimulation = async (branchCode: BranchCode) => {
    setIsCreatingSimulation(true);
    try {
      if (branchCode === BranchCode.Borrower) {
        const createResponse = await borrowersAPI.createBorrowerProject();
        const borrowerProjectId = createResponse.id
        if (!borrowerProjectId)
          return
        const fetchResponse = await borrowersAPI.getBorrowerProjectById({ borrowerProjectId });
        const folderIdentifier = fetchResponse.project?.folderIdentifier

        if(folderIdentifier)
        dispatch(initSubscriptionProcess({ branchCode, riskId: borrowerProjectId, folderIdentifier }));
        navigateToSimulation(branchCode, borrowerProjectId);
        closeDialog();
      } else {
        const riskId = await fetchRisk(branchCode)
        navigateToSimulation(branchCode, riskId);
        closeDialog();
      }
    } catch (error) {
      console.error('Erreur lors de la création de la simulation:', error);
    } finally {
      setIsCreatingSimulation(false);
    }
  };

  const handleBranchClick = async (branchCode: BranchCode) => {
    setSelectedBranch(branchCode);
    setShowSimulationsList(false);

    // Récupérer les simulations pour cette branche
    await getSimulationsInProgress(branchCode);
  };

  const handleActionSelect = async (action: ActionType) => {
    if (!selectedBranch) return;

    switch (action) {
      case 'new':
        await createNewSimulation(selectedBranch);
        break;
      case 'continue':
        // Continuer avec la dernière simulation (la plus récente)
        const latestSimulation = simulationsInProgress
          .sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime())[0];

        if (latestSimulation) {
          // Just navigate - SubscriptionProcessWrapper will fetch the data
          navigateToSimulation(selectedBranch, latestSimulation.riskId);
          closeDialog();
        }
        break;
      case 'list':
        setShowSimulationsList(true);
        break;
    }
  };

  const handleSimulationSelect = async (simulation: SimulationInProgress) => {
    // Just navigate - SubscriptionProcessWrapper will fetch the data
    navigateToSimulation(selectedBranch!, simulation.riskId);
    closeDialog();
  };

  const handleBackToActions = () => {
    setShowSimulationsList(false);
  };

  const handleBackToBranches = () => {
    setSelectedBranch(null);
    setShowSimulationsList(false);
    setSimulationsInProgress([]);
  };

  const getIconAndColor = useCallback((branchCode: BranchCode) => {
    switch (branchCode) {
      case BranchCode.Unemployment:
        return {
          icon: <Briefcase className="w-8 h-8" />,
          gradient: "from-red-500 to-pink-500",
          bgColor: "bg-red-50",
          hoverBg: "hover:bg-red-100",
          description: "Protection en cas de perte d'emploi"
        };
      case BranchCode.UPI:
        return {
          icon: <Home className="w-8 h-8" />,
          gradient: "from-blue-500 to-cyan-500",
          bgColor: "bg-blue-50",
          hoverBg: "hover:bg-blue-100",
          description: "Protection de votre logement"
        };
      case BranchCode.Borrower:
        return {
          icon: <Wallet className="w-8 h-8" />,
          gradient: "from-green-500 to-emerald-500",
          bgColor: "bg-green-50",
          hoverBg: "hover:bg-green-100",
          description: "Sécurisez votre crédit immobilier"
        };
      case BranchCode.Mri:
        return {
          icon: <Home className="w-8 h-8" />,
          gradient: "from-purple-500 to-indigo-500",
          bgColor: "bg-purple-50",
          hoverBg: "hover:bg-purple-100",
          description: "Multirisque Immeuble"
        };
      case BranchCode.RGI:
        return {
          icon: <Shield className="w-8 h-8" />,
          gradient: "from-orange-500 to-amber-500",
          bgColor: "bg-orange-50",
          hoverBg: "hover:bg-orange-100",
          description: "Garantie Loyers Impayés"
        };
      case BranchCode.LegalProtection:
        return {
          icon: <Scale className="w-8 h-8" />,
          gradient: "from-teal-500 to-sky-500",
          bgColor: "bg-teal-50",
          hoverBg: "hover:bg-teal-100",
          description: "Protection de votre activité professionnelle"
        };
      default:
        return {
          icon: <Wallet className="w-8 h-8" />,
          gradient: "from-gray-500 to-slate-500",
          bgColor: "bg-gray-50",
          hoverBg: "hover:bg-gray-100",
          description: "Service par défaut"
        };
    }
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Vue principale - sélection des branches
  if (!selectedBranch) {
    return (
      <div className="max-w-6xl mx-auto p-10">
        {/* En-tête */}
        <div className="text-center mt-2 mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Quelle assurance souhaitez-vous proposer ?
          </h2>
          <p className="text-gray-600 text-lg">
            Sélectionnez l'assurance qui correspond à vos besoins
          </p>
        </div>

        {isLoading && (
          <div className="flex justify-center">
            <Loading loading={isLoading} />
          </div>
        )}

        {!isLoading && branches && branches.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {branches?.map((branch) => {
              const { icon, gradient, bgColor, hoverBg, description } = getIconAndColor(
                branch.code as BranchCode
              );
              const isHovered = hoveredBranch === branch.code;

              return (
                <div
                  key={branch.code}
                  className={`group relative cursor-pointer rounded-2xl border-2 border-gray-100 ${bgColor} ${hoverBg} 
                           transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-xl
                           ${isHovered ? 'shadow-xl scale-[1.02]' : 'shadow-lg'}`}
                  onClick={() => handleBranchClick(branch.code as BranchCode)}
                  onMouseEnter={() => setHoveredBranch(branch.code)}
                  onMouseLeave={() => setHoveredBranch(null)}
                >
                  {/* Overlay gradient au survol */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 
                             rounded-2xl transition-opacity duration-300`}
                  />

                  {/* Contenu */}
                  <div className="relative p-8 text-center">
                    {/* Conteneur d'icône avec arrière-plan dégradé */}
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-full 
                               bg-gradient-to-br ${gradient} text-white mb-4 
                               shadow-lg group-hover:shadow-xl transition-all duration-300
                               group-hover:scale-110`}
                    >
                      {icon}
                    </div>

                    {/* Titre */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-gray-800">
                      {t(branch.label)}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                      {description}
                    </p>

                    {/* Indicateur de flèche */}
                    <div className="flex items-center justify-center">
                      <div className="flex items-center text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
                        <span className="text-sm font-medium mr-2">Commencer</span>
                        <ChevronRight
                          className={`w-5 h-5 transition-all duration-300 group-hover:translate-x-1`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {branches?.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Briefcase className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Aucune branche disponible
            </h3>
            <p className="text-gray-500">
              Veuillez réessayer plus tard ou contacter le support.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Vue de la liste des simulations
  if (showSimulationsList) {
    const selectedBranchData = branches?.find(b => b.code === selectedBranch);
    const { icon, gradient } = getIconAndColor(selectedBranch);

    return (
      <div className="max-w-4xl mx-auto p-10">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full 
                         bg-gradient-to-br ${gradient} text-white mb-4 shadow-lg`}>
            {icon}
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Simulations en cours - {selectedBranchData ? t(selectedBranchData.label) : ''}
          </h2>
          <p className="text-gray-600">
            Sélectionnez une simulation existante pour la reprendre
          </p>
        </div>

        {isLoadingSimulations && (
          <div className="flex justify-center">
            <Loading loading={isLoadingSimulations} />
          </div>
        )}

        {!isLoadingSimulations && (
          <div className="space-y-4 mb-8">
            {simulationsInProgress.map((simulation, index) => (
              <div
                key={simulation.riskId}
                className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow duration-200"
                onClick={() => handleSimulationSelect(simulation)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
                      <Clock className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {simulation.label || `Dossier ${simulation.folderIdentifier}`}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Créé le {formatDate(simulation.creationDate.toString())}
                      </p>
                      <p className="text-xs text-blue-600">
                        ID: {simulation.riskId}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-center space-x-4">
          <button
            onClick={handleBackToActions}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
          >
            Retour aux options
          </button>
        </div>
      </div>
    );
  }

  // Vue des actions (nouvelle simulation, continuer, liste)
  const selectedBranchData = branches?.find(b => b.code === selectedBranch);
  const { icon, gradient } = getIconAndColor(selectedBranch);

  return (
    <div className="max-w-4xl mx-auto p-10">
      <div className="text-center mb-10">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full 
                       bg-gradient-to-br ${gradient} text-white mb-4 shadow-lg`}>
          {icon}
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {selectedBranchData ? t(selectedBranchData.label) : ''}
        </h2>
        <p className="text-gray-600">
          Comment souhaitez-vous procéder ?
        </p>
        {simulationsInProgress.length > 0 && (
          <p className="text-blue-600 text-sm mt-2">
            {simulationsInProgress.length} simulation(s) en cours trouvée(s) pour cette branche
          </p>
        )}
      </div>

      {(isLoadingSimulations || isCreatingSimulation) && (
        <div className="flex justify-center">
          <Loading loading={isLoadingSimulations || isCreatingSimulation} />
        </div>
      )}

      {!isLoadingSimulations && !isCreatingSimulation && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Nouvelle simulation */}
            <div
              className="group cursor-pointer rounded-xl border-2 border-gray-100 bg-green-50 hover:bg-green-100 
                       transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-xl p-8 text-center"
              onClick={() => handleActionSelect('new')}
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full 
                           bg-gradient-to-br from-green-500 to-emerald-500 text-white mb-4 
                           shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                <Plus className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nouvelle simulation
              </h3>
              <p className="text-gray-600 text-sm">
                Commencer une toute nouvelle simulation
              </p>
            </div>

            {/* Continuer la dernière */}
            {simulationsInProgress.length > 0 && (() => {
              const latestSimulation = simulationsInProgress
                .sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime())[0];

              return (
                <div
                  className="group cursor-pointer rounded-xl border-2 border-gray-100 bg-blue-50 hover:bg-blue-100 
                           transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-xl p-8 text-center"
                  onClick={() => handleActionSelect('continue')}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full 
                               bg-gradient-to-br from-blue-500 to-cyan-500 text-white mb-4 
                               shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                    <Play className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Reprendre la dernière
                  </h3>
                  <p className="text-gray-600 text-sm mb-2">
                    {latestSimulation.label || `Dossier ${latestSimulation.folderIdentifier}`}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Créée le {formatDate(latestSimulation.creationDate.toString())}
                  </p>
                </div>
              );
            })()}

            {/* Liste des simulations */}
            {simulationsInProgress.length > 1 && (
              <div
                className="group cursor-pointer rounded-xl border-2 border-gray-100 bg-purple-50 hover:bg-purple-100 
                         transition-all duration-300 ease-in-out transform hover:scale-[1.02] hover:shadow-xl p-8 text-center"
                onClick={() => handleActionSelect('list')}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full 
                             bg-gradient-to-br from-purple-500 to-indigo-500 text-white mb-4 
                             shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                  <List className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Voir toutes les simulations
                </h3>
                <p className="text-gray-600 text-sm">
                  Choisir parmi vos {simulationsInProgress.length} simulations
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleBackToBranches}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            >
              Retour aux assurances
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectBrokerBranches
